import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import styles from './Navbar.module.css';
import SearchOverlay from './SearchOverlay';

// ── Image assets ──────────────────────────────────────────────────────────────
const LOGO_WHITE   = 'https://res.cloudinary.com/dhjzovpof/image/upload/v1773094526/2d84a764693362c80d1e3dc38f7b339504b19d2e_iobboq.png';
const LOGO_COLOR   = 'https://res.cloudinary.com/dhjzovpof/image/upload/v1773094633/dbae1ac767fb73178c9e75eafff0eb62f069bf78_layuwm.png';
const HAMBURGER_DARK = 'https://res.cloudinary.com/dhjzovpof/image/upload/v1773094686/menu_line_znrlqk.png';
const HAMBURGER_COLOR = 'https://res.cloudinary.com/dhjzovpof/image/upload/v1773094813/menu_line_gah8eb.svg';
// const HAMBURGER_WHITE = 'https://res.cloudinary.com/dhjzovpof/image/upload/v1777814564/Vector_1_w08hua.png';
// Desktop nav links
const NAV_LINKS = [
  {
    label: 'Who We Are', to: '/about', hasArrow: true,
    submenu: [
      { label: 'About', to: '/about' },
      { label: 'Team', to: '/team'  },
      {label: 'Tektron Group', to: '/group'}
    ],
  },
  { label: 'Services',   to: '/services', hasArrow: false },
  { label: 'Projects',  to: '/projects', hasArrow: false },
  { label: 'Tektron Thermo',   to: '/thermo', hasArrow: false },

  {
    label: 'Our Impact', to: '/impact', hasArrow: true,
    submenu: [
      { label: 'Impact', to: '/impact'   },
    ],
  },
  { label: 'Partners', to: '/partners', hasArrow: false },
  { label: 'Contact',  to: '/contact',  hasArrow: false },
];

// Mobile nav items (matches Figma exactly)
const MOBILE_NAV = [
  {
    label: 'Who We Are', to: '/about',
    dropdown: [
      { label: 'About', to: '/about' },
      { label: 'Team', to: '/team'  },
      {label: 'Tektron Group', to: '/group'}
    ],
  },

  { label: 'Services',   to: '/services' },
  { label: 'Projects', to: '/projects' },
  { label: 'Tektron Thermo',   to: '/thermo', hasArrow: false },

  {
    label: 'Our Impact', to: '/impact',
    dropdown: [
      { label: 'Impact',  to: '/impact' },
    ],
  },
  { label: 'Partners',   to: '/partners' },
  { label: 'Contact',    to: '/contact' },
];

const menuVariants = {
  hidden:  { x: '100%' },
  visible: { x: 0,     transition: { type: 'tween', duration: 0.28, ease: [0.4, 0, 0.2, 1] } },
  exit:    { x: '100%', transition: { type: 'tween', duration: 0.24, ease: [0.4, 0, 0.2, 1] } },
};

const ctaDropdownVariants = {
  hidden:  { opacity: 0, translateY: -4 },
  visible: { opacity: 1, translateY: 0, transition: { type: 'tween', duration: 0.15, ease: [0.4, 0, 0.2, 1] } },
  exit:    { opacity: 0, translateY: -4, transition: { type: 'tween', duration: 0.12, ease: [0.4, 0, 0.2, 1] } },
};

/**
 * Shared Navbar
 * @param {'dark'|'light'} theme  - dark = Landing (transparent/absolute), light = inner pages (white/sticky)
 * @param {string} cta            - CTA button label (desktop only)
 * @param {string} ctaTo          - Route for CTA button
 */
export default function Navbar({ theme = 'light', cta = 'Contact Us', ctaTo = '/contact' }) {
  const [open, setOpen]             = useState(false);
  const [openMobileSubmenu, setOpenMobileSubmenu] = useState(null);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [openCtaDropdown, setOpenCtaDropdown] = useState(false);
  const ctaDropdownRef = useRef(null);
  const isDark = theme === 'dark';
  const logo   = isDark ? LOGO_WHITE : LOGO_COLOR;

  const toggleSearch = () => {
    setSearchOpen(v => !v);
    setOpen(false);
    setOpenMobileSubmenu(null);
  };

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Close CTA dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (ctaDropdownRef.current && !ctaDropdownRef.current.contains(e.target)) {
        setOpenCtaDropdown(false);
      }
    }
    if (openCtaDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openCtaDropdown]);

  const closeMenu = () => { setOpen(false); setOpenMobileSubmenu(null); };

  return (
      <>
        {/* ── Desktop / tablet nav ─────────────────────────────────────────── */}
        <nav className={`${styles.navbar} ${isDark ? styles.navbarDark : styles.navbarLight}`}>
          <div className={styles.navInner}>
            <Link to="/" className={styles.logoLink}>
              <img
                  src={logo}
                  alt="TektronPlus"
                  className={`${styles.navLogo} ${isDark ? styles.navLogoDark : styles.navLogoLight}`}
              />
            </Link>

            <div className={styles.navLinks}>
              {NAV_LINKS.map(({ label, to, submenu }) =>
                  submenu ? (
                      <div
                          key={label}
                          className={styles.navLinkWrapper}
                          onMouseEnter={() => setOpenSubmenu(label)}
                          onMouseLeave={() => setOpenSubmenu(null)}
                      >
                        <Link
                            to={to}
                            className={`${styles.navLink} ${isDark ? styles.navLinkDark : styles.navLinkLight}`}
                        >
                          {label}
                        </Link>
                        {/* Tappable caret — lets tablet users toggle without navigating */}
                        <button
                            className={`${styles.caretBtn} ${isDark ? styles.caretBtnDark : styles.caretBtnLight}`}
                            onClick={() => setOpenSubmenu(v => v === label ? null : label)}
                            aria-label={`Toggle ${label} submenu`}
                            aria-expanded={openSubmenu === label}
                        >
                          &nbsp;
                          <svg
                              width="12" height="8" viewBox="0 0 7.65757 4.82879" fill="none"
                              className={`${styles.navArrow} ${styles.caret} ${openSubmenu === label ? styles.caretOpen : ''}`}
                              aria-hidden="true"
                          >
                            <path fillRule="evenodd" clipRule="evenodd" d="M4.53579 4.536C4.34826 4.72347 4.09395 4.82879 3.82879 4.82879C3.56362 4.82879 3.30932 4.72347 3.12179 4.536L0.292787 1.707C0.152917 1.56709 0.0576906 1.38883 0.0191549 1.19479C-0.0193808 1.00074 0.000506029 0.799624 0.0763 0.616884C0.152094 0.434143 0.280389 0.27799 0.444952 0.168182C0.609516 0.0583735 0.802952 -0.00015534 1.00079 3.09641e-07H6.65679C6.85462 -0.00015534 7.04806 0.0583735 7.21262 0.168182C7.37719 0.27799 7.50548 0.434143 7.58127 0.616884C7.65707 0.799624 7.67696 1.00074 7.63842 1.19479C7.59988 1.38883 7.50466 1.56709 7.36479 1.707L4.53579 4.536Z" fill="currentColor"/>
                          </svg>
                        </button>
                        <div className={`${styles.desktopDropdown} ${openSubmenu === label ? styles.desktopDropdownOpen : ''}`}>
                          <div className={styles.desktopDropdownInner}>
                            {submenu.map(sub => (
                                <Link
                                    key={sub.label}
                                    to={sub.to}
                                    className={styles.desktopDropdownItem}
                                    onClick={() => setOpenSubmenu(null)}
                                >
                                  {sub.label}
                                </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                  ) : (
                      <Link
                          key={label}
                          to={to}
                          className={`${styles.navLink} ${isDark ? styles.navLinkDark : styles.navLinkLight}`}
                      >
                        {label}
                      </Link>
                  )
              )}
            </div>

            {/* ── CTA Button with Dropdown ── */}
            <div
                ref={ctaDropdownRef}
                className={`${styles.ctaWrapper} ${openCtaDropdown ? styles.ctaWrapperOpen : ''}`}
            >
              <Link
                  to={ctaTo}
                  className={`${styles.ctaBtn} ${isDark ? styles.ctaBtnDark : styles.ctaBtnLight}`}
              >
                {cta}
              </Link>


              {/* CTA Dropdown Menu */}
              <AnimatePresence>
                {openCtaDropdown && (
                    <motion.div
                        className={styles.ctaDropdown}
                        variants={ctaDropdownVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                    >
                      <Link
                          to="/contact"
                          className={styles.ctaDropdownItem}
                          onClick={() => setOpenCtaDropdown(false)}
                      >
                        Contact
                      </Link>
                    </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className={styles.mobileActions}>
              {searchOpen ? (
                  <button
                      className={styles.mobileSearchClose}
                      aria-label="Close search"
                      onClick={toggleSearch}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="M3.5 3.5L12.5 12.5M3.5 12.5L12.5 3.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round"/>
                    </svg>
                  </button>
              ) : (
                  <button className={styles.mobileSearchBtn} aria-label="Search" onClick={toggleSearch}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <circle cx="10.5" cy="10.5" r="6.25" stroke={isDark ? '#fff' : '#CF181F'} strokeWidth="1.5"/>
                      <path d="M15.5 15.5L20 20" stroke={isDark ? '#fff' : '#CF181F'} strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </button>
              )}
              <button
                  className={styles.hamburger}
                  aria-label="Open menu"
                  onClick={() => { setOpen(true); setSearchOpen(false); }}
              >
                <img
                    src={isDark ? HAMBURGER_DARK : HAMBURGER_COLOR}
                    alt=""
                />
              </button>
            </div>
          </div>

        </nav>

        <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />

        {/* ── Mobile menu overlay ──────────────────────────────────────────── */}
        <AnimatePresence>
          {open && (
              <motion.div
                  className={styles.mobileMenu}
                  variants={menuVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
              >
                {/* Header */}
                <div className={styles.mobileMenuHeader}>
                  <Link to="/" onClick={closeMenu} className={styles.logoLink}>
                    <img src={LOGO_COLOR} alt="TektronPlus" className={styles.mobileMenuLogo} />
                  </Link>
                  <div className={styles.mobileMenuHeaderRight}>
                    <button className={styles.searchBtn} aria-label="Search" onClick={toggleSearch}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle cx="10.5" cy="10.5" r="6.25" stroke="#5e5e5e" strokeWidth="1.5"/>
                        <path d="M15.5 15.5L20 20" stroke="#5e5e5e" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </button>
                    <button className={styles.closeBtn} aria-label="Close menu" onClick={closeMenu}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M6.5 6.5L17.5 17.5M6.5 17.5L17.5 6.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round"/>
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Nav links */}
                <nav className={styles.mobileMenuLinks}>
                  {MOBILE_NAV.map(({ label, to, dropdown }, i) => (
                      <div
                          key={label}
                          className={`${styles.mobileMenuRow} ${i === 0 ? styles.mobileMenuRowFirst : ''}`}
                      >
                        {dropdown ? (
                            <>
                              <button
                                  className={styles.mobileMenuItemToggle}
                                  onClick={() => setOpenMobileSubmenu(v => v === label ? null : label)}
                              >
                                <span>{label}</span>
                                <svg
                                    width="16" height="16" viewBox="0 0 16 16" fill="none"
                                    className={`${styles.caret} ${openMobileSubmenu === label ? styles.caretOpen : ''}`}
                                    aria-hidden="true"
                                >
                                  <path d="M3.5 5.5L8 10.5L12.5 5.5" stroke="#5e5e5e" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                              <AnimatePresence>
                                {openMobileSubmenu === label && (
                                    <motion.div
                                        className={styles.mobileSubItems}
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1, transition: { duration: 0.2 } }}
                                        exit={{ height: 0, opacity: 0, transition: { duration: 0.15 } }}
                                    >
                                      {dropdown.map(sub => (
                                          <Link
                                              key={sub.label}
                                              to={sub.to}
                                              className={styles.mobileSubItem}
                                              onClick={closeMenu}
                                          >
                                            {sub.label}
                                          </Link>
                                      ))}
                                    </motion.div>
                                )}
                              </AnimatePresence>
                            </>
                        ) : (
                            <Link to={to} className={styles.mobileMenuLink} onClick={closeMenu}>
                              {label}
                            </Link>
                        )}
                      </div>
                  ))}
                </nav>

                {/* Gradient + CTA */}
                <div className={styles.mobileMenuBottom}>
                  <Link to="/contact#consultancy_form" className={styles.mobileMenuCta} onClick={closeMenu}>
                    Request Consultancy
                  </Link>
                </div>
              </motion.div>
          )}
        </AnimatePresence>
      </>
  );
}
